import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { ErrorToast } from '../toasters/toasters';
import { formatDateToYYYYMMDDLocal } from '../utils/functions';

export interface CsvExportParams {
  fromDate?: string;
  toDate?: string;
}

interface CsvExportModalProps {
  show: boolean;
  onHide: () => void;
  title: string;
  // e.g. "users" -> dozentify-users-2026-10-19.csv
  fileNamePrefix: string;
  exportCsv: (params: CsvExportParams) => Promise<AxiosResponse<Blob>>;
}

function toExportParams(fromDate: Date | null, toDate: Date | null): CsvExportParams {
  return {
    fromDate: fromDate ? formatDateToYYYYMMDDLocal(fromDate) : undefined,
    toDate: toDate ? formatDateToYYYYMMDDLocal(toDate) : undefined,
  };
}

function saveCsv(data: Blob, fileName: string) {
  const url = URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Both dates are optional: the backend exports everything when they are left empty.
const CsvExportModal = ({ show, onHide, title, fileNamePrefix, exportCsv }: CsvExportModalProps) => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await exportCsv(toExportParams(fromDate, toDate));
      const today = formatDateToYYYYMMDDLocal(new Date());
      saveCsv(response.data, `dozentify-${fileNamePrefix}-${today}.csv`);
      onHide();
    } catch (error) {
      ErrorToast(error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">{t('csvExport.hint')}</p>
        <div className="row g-3">
          <div className="col-sm-6">
            <label className="form-label">{t('csvExport.fromDate')}</label>
            <DatePicker
              selected={fromDate}
              onChange={(date: Date | null) => setFromDate(date)}
              maxDate={toDate ?? undefined}
              placeholderText={t('csvExport.fromDate')}
              className="form-control"
              dateFormat="dd.MM.yyyy"
              isClearable
            />
          </div>
          <div className="col-sm-6">
            <label className="form-label">{t('csvExport.toDate')}</label>
            <DatePicker
              selected={toDate}
              onChange={(date: Date | null) => setToDate(date)}
              minDate={fromDate ?? undefined}
              placeholderText={t('csvExport.toDate')}
              className="form-control"
              dateFormat="dd.MM.yyyy"
              isClearable
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isExporting}>
          {t('common.cancel')}
        </Button>
        <Button variant="success" onClick={handleExport} disabled={isExporting}>
          <i className="ti ti-file-export f-18"></i>{' '}
          {isExporting ? t('common.loading') : t('csvExport.export')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CsvExportModal;
